import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Button } from "@/admin/ui";
import { ROLE_LABEL } from "@/user/dashboards.config";
import { formsForRole, COMING_SOON } from "@/user/workspace/forms.config";
import SubmissionForm from "@/user/workspace/SubmissionForm";
import { PageHeader, ComingSoonGrid, ActionIcon, StatusPill, PriorityPill } from "@/user/workspace/wsui";
import { ws, fmtDate } from "@/user/workspace/ws.api";

export default function MyWorkspace() {
    const { user } = useOutletContext();
    const nav = useNavigate();
    const forms = formsForRole(user.role);
    const [active, setActive] = useState(null);
    const [summary, setSummary] = useState(null);

    const load = () => ws.summary().then(setSummary).catch(() => {});
    useEffect(() => { load(); }, []);

    const cards = [
        { label: "Total Submissions", value: summary?.total ?? "—" },
        { label: "Submitted", value: summary?.submitted ?? "—" },
        { label: "Pending Drafts", value: summary?.drafts ?? "—", tone: "warning" },
        { label: "This Week", value: summary?.this_week ?? "—" },
        { label: "Files Uploaded", value: summary?.files ?? "—" },
        { label: "Tasks Pending", value: summary?.tasks_pending ?? "—", tone: "warning" },
    ];

    return (
        <div data-testid="page-workspace">
            <PageHeader
                eyebrow={ROLE_LABEL[user.role] || user.role}
                title="My Workspace"
                subtitle="Update your department in seconds. Every submission becomes structured data for the CEO."
            />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-9">
                {cards.map((c, i) => (
                    <div key={i} data-testid={`ws-kpi-${i}`} className="glass rounded-2xl p-4">
                        <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500">{c.label}</div>
                        <div className={`mt-2 font-display text-2xl font-medium leading-none ${c.tone === "warning" ? "text-amber-600" : "text-black"}`}>{c.value}</div>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-medium text-black">Quick Actions</h2>
                <Button variant="ghost" size="sm" onClick={() => nav("/app/submissions")} data-testid="ws-view-all">View all submissions →</Button>
            </div>

            {forms.length === 0 ? (
                <div className="glass-strong rounded-3xl p-10 text-center text-neutral-500">
                    Your role reviews data rather than submitting it. Head to your dashboard for the command center.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {forms.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setActive(f)}
                            data-testid={`ws-form-${f.key}`}
                            className="group text-left glass rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-20px_rgba(139,92,246,0.28)] transition-all duration-300"
                        >
                            <div className="flex items-center justify-between">
                                <span className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                                    <ActionIcon name={f.icon} />
                                </span>
                                <svg className="text-neutral-300 group-hover:text-violet-500 transition-colors" width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </div>
                            <div className="mt-4 font-display text-[17px] font-medium text-black">{f.label}</div>
                            <p className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed">{f.desc}</p>
                        </button>
                    ))}
                </div>
            )}

            {summary?.recent?.length > 0 && (
                <div className="mt-10">
                    <h2 className="font-display text-lg font-medium text-black mb-4">Recent Updates</h2>
                    <div className="glass rounded-2xl divide-y divide-black/5 overflow-hidden">
                        {summary.recent.map((s) => (
                            <button key={s.id} onClick={() => nav("/app/submissions")} data-testid={`ws-recent-${s.id}`} className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-black/[0.02] text-left">
                                <div className="min-w-0">
                                    <div className="text-[13.5px] font-medium text-black truncate">{s.title}</div>
                                    <div className="text-[11px] text-neutral-400 font-mono-alt">{s.submission_id} · {fmtDate(s.updated_at)}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <PriorityPill priority={s.priority} />
                                    <StatusPill status={s.status} />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <ComingSoonGrid items={COMING_SOON} />

            <SubmissionForm
                form={active}
                role={user.role}
                open={!!active}
                onClose={() => setActive(null)}
                onSaved={load}
            />
        </div>
    );
}
